import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useStaffChatSocket } from '../context/StaffChatSocketContext.jsx';
import { apiJson } from '../api/client.js';
import { AdminChatSettings } from '../components/AdminChatSettings.jsx';
import { ChatPanel } from '../components/ChatPanel.jsx';

function upsertConversation(list, conversation) {
  if (!conversation?.id) return list;
  const rest = list.filter((c) => c.id !== conversation.id);
  return [conversation, ...rest].sort(
    (a, b) => new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime(),
  );
}

export function AdminChatPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'settings' ? 'settings' : 'inbox';
  const canManageSettings = user?.capabilities?.manage_chat_settings;

  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [unreadIds, setUnreadIds] = useState(() => new Set());
  const [error, setError] = useState('');
  const activeIdRef = useRef(activeId);

  activeIdRef.current = activeId;

  const {
    connected,
    connecting,
    messages,
    conversation,
    error: socketErr,
    sendMessage,
    appendMessage,
    joinConversation,
    subscribeInbox,
  } = useStaffChatSocket();

  const loadList = useCallback(() => {
    apiJson('/api/admin/chat/conversations?status=open')
      .then((r) => setConversations(r.conversations || []))
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (tab === 'inbox') loadList();
  }, [tab, loadList]);

  useEffect(() => {
    const fromUrl = parseInt(searchParams.get('conversation') || '', 10);
    if (fromUrl) setActiveId(fromUrl);
  }, [searchParams]);

  useEffect(() => {
    if (tab === 'inbox') {
      joinConversation(activeId);
    } else {
      joinConversation(null);
    }
  }, [tab, activeId, joinConversation]);

  useEffect(() => {
    return subscribeInbox(({ conversation_id: conversationId, message, conversation: conv }) => {
      if (conv) {
        setConversations((prev) => upsertConversation(prev, conv));
      } else if (message) {
        setConversations((prev) => {
          const existing = prev.find((c) => c.id === conversationId);
          if (!existing) {
            loadList();
            return prev;
          }
          return upsertConversation(prev, {
            ...existing,
            updated_at: message.created_at || existing.updated_at,
          });
        });
      } else if (conversationId) {
        loadList();
      }

      if (conversationId && conversationId !== activeIdRef.current && message) {
        setUnreadIds((prev) => new Set(prev).add(conversationId));
      }

      if (conversationId === activeIdRef.current && message) {
        appendMessage(message);
      }
    });
  }, [subscribeInbox, appendMessage, loadList]);

  const select = (id) => {
    setActiveId(id);
    setUnreadIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setSearchParams({ conversation: String(id) }, { replace: true });
  };

  return (
    <div>
      <div className="row-actions" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, flex: 1 }} data-tour="page-title">
          Live chat
          {tab === 'inbox' && connected ? (
            <span className="muted small" style={{ marginLeft: 8, fontWeight: 400 }}>
              · online
            </span>
          ) : null}
        </h2>
        <Link
          to="/admin/chat"
          className={tab === 'inbox' ? 'btn-primary' : 'btn-secondary'}
          style={{ textDecoration: 'none' }}
        >
          Inbox
        </Link>
        {canManageSettings ? (
          <Link
            to="/admin/chat?tab=settings"
            className={tab === 'settings' ? 'btn-primary' : 'btn-secondary'}
            style={{ textDecoration: 'none' }}
          >
            Settings
          </Link>
        ) : null}
      </div>

      {tab === 'settings' && canManageSettings ? (
        <div className="auth-card flat">
          <AdminChatSettings />
        </div>
      ) : null}

      {tab === 'inbox' ? (
        <>
          {error ? <p className="err">{error}</p> : null}
          {socketErr ? <p className="err">{socketErr}</p> : null}
          <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16 }} data-tour="chat-panel">
            <div className="auth-card flat">
              <strong>Open conversations</strong>
              <ul style={{ listStyle: 'none', padding: 0, marginTop: 12 }}>
                {conversations.map((c) => (
                  <li key={c.id} style={{ marginBottom: 8 }}>
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => select(c.id)}
                      style={{
                        textAlign: 'left',
                        width: '100%',
                        fontWeight: unreadIds.has(c.id) ? 700 : 400,
                      }}
                    >
                      #{c.id} {c.client_name || c.guest_name || 'Guest'}
                      {unreadIds.has(c.id) ? ' · new' : ''}
                      {c.ai_active ? ' · AI' : ''}
                    </button>
                  </li>
                ))}
              </ul>
              {conversations.length === 0 ? <p className="muted small">No open chats</p> : null}
            </div>
            <div>
              {activeId ? (
                <>
                  <ChatPanel
                    title={conversation?.client_name || conversation?.guest_name || `Conversation #${activeId}`}
                    messages={messages}
                    connected={connected}
                    connecting={connecting}
                    onSend={sendMessage}
                    onAttachmentUploaded={appendMessage}
                    conversationId={activeId}
                  />
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ marginTop: 8 }}
                    onClick={async () => {
                      await apiJson(`/api/admin/chat/conversations/${activeId}/close`, { method: 'POST' });
                      setActiveId(null);
                      setSearchParams({}, { replace: true });
                      joinConversation(null);
                      loadList();
                    }}
                  >
                    Close conversation
                  </button>
                </>
              ) : (
                <p className="muted">
                  Select a conversation. New client messages appear here in real time while you are in the
                  admin area.
                </p>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
